/**
 * Fractional ranking keys.
 *
 * Spec v0.5 §5.3 decision 2: a drag writes one row, not a renumbered backlog.
 * Ranks are short base-36 strings compared lexicographically, so a new rank can
 * always be produced between any two neighbours without touching them.
 *
 * `requests` carries two of these — `backlog_rank` and `board_rank` — because
 * one column cannot serve two independently ordered lists.
 */
const ALPHABET = '0123456789abcdefghijklmnopqrstuvwxyz';
const BASE = ALPHABET.length;
/** Ranks never end in the lowest digit: it has no midpoint below it. */
const LOWEST = ALPHABET[0];
function digit(char) {
    const index = ALPHABET.indexOf(char);
    if (index < 0)
        throw new RangeError(`invalid rank character: ${JSON.stringify(char)}`);
    return index;
}
function assertValidRank(rank, label) {
    if (rank === '')
        return;
    for (const char of rank)
        digit(char);
    if (rank.endsWith(LOWEST)) {
        throw new RangeError(`${label} must not end with '${LOWEST}': ${rank}`);
    }
}
/**
 * The shortest string strictly between `lower` and `upper`.
 * `lower === ''` means "no lower bound"; `upper === null` means "no upper bound".
 */
function midpoint(lower, upper) {
    if (upper !== null && lower >= upper) {
        throw new RangeError(`rank bounds out of order: ${JSON.stringify(lower)} >= ${JSON.stringify(upper)}`);
    }
    if (upper !== null) {
        // Walk past the shared prefix and recurse on what differs.
        let shared = 0;
        while ((lower[shared] ?? LOWEST) === upper[shared])
            shared += 1;
        if (shared > 0) {
            return upper.slice(0, shared) + midpoint(lower.slice(shared), upper.slice(shared));
        }
    }
    const low = lower === '' ? 0 : digit(lower[0]);
    const high = upper === null ? BASE : digit(upper[0]);
    if (high - low > 1) {
        // There is room for a digit between them.
        return ALPHABET[Math.round(0.5 * (low + high))];
    }
    if (upper !== null && upper.length > 1) {
        // Adjacent digits, but `upper` has more to give: borrow its first digit.
        return upper.slice(0, 1);
    }
    // Adjacent digits with nothing to borrow: extend `lower` by one place.
    return ALPHABET[low] + midpoint(lower.slice(1), null);
}
/**
 * A rank strictly between `before` and `after`.
 *
 * Pass `null` for either end: `rankBetween(null, first)` prepends,
 * `rankBetween(last, null)` appends, `rankBetween(null, null)` seeds an empty list.
 */
export function rankBetween(before, after) {
    if (before !== null)
        assertValidRank(before, 'before');
    if (after !== null)
        assertValidRank(after, 'after');
    if (before !== null && after !== null && before >= after) {
        throw new RangeError(`before must sort before after: ${before} >= ${after}`);
    }
    return midpoint(before ?? '', after);
}
/** `count` evenly spread ranks, for seeding a list in one go. */
export function rankSequence(count) {
    if (!Number.isInteger(count) || count < 0) {
        throw new RangeError(`count must be a non-negative integer, got ${count}`);
    }
    const ranks = [];
    let previous = null;
    for (let index = 0; index < count; index += 1) {
        previous = rankBetween(previous, null);
        ranks.push(previous);
    }
    return ranks;
}
/** Comparator for sorting by rank ascending. Ranks are plain strings. */
export function compareRank(a, b) {
    return a < b ? -1 : a > b ? 1 : 0;
}
//# sourceMappingURL=rank.js.map